// El sonido de los avisos de Santi en la oficina abierta (04-11, Pau: «yo quiero poder elegir el sonido y tal»). Cinco,
// sintetizados (lib/sonido.ts, sin ficheros), y se elige por aparato (este navegador: localStorage). Por defecto,
// Campanita. El aviso del sistema (Windows, el Mac) suena con el sonido del sistema: eso no se elige desde la web.
// 04-11 (Pau: «suenan muy flojito»): las notas van fuertes (picos ~0,8 tras el compresor de lib/sonido.ts, como un aviso
// de Windows o del Mac) y el «Volumen» de «Avisos y sonido» (0-100 %, 80 % por defecto, por aparato) los escala.
import { useCallback, useEffect, useState } from 'react';
import { tocarNotas, type Nota } from '../../../lib/sonido';

export type SonidoAviso = 'campanita' | 'gota' | 'doble' | 'suave' | 'ninguno';

export const SONIDOS: ReadonlyArray<{ id: SonidoAviso; notas: readonly Nota[] }> = [
  // Dos golpes de campana (mi y la agudos), con un armónico que la hace brillar y una cola larga.
  {
    id: 'campanita',
    notas: [
      { frecuencia: 1318.5, inicio: 0, duracion: 0.9, volumen: 0.7, ataque: 0.004 },
      { frecuencia: 2637, inicio: 0, duracion: 0.45, volumen: 0.22, ataque: 0.004 },
      { frecuencia: 1760, inicio: 0.15, duracion: 1.1, volumen: 0.65, ataque: 0.004 },
      { frecuencia: 3520, inicio: 0.15, duracion: 0.5, volumen: 0.19, ataque: 0.004 },
    ],
  },
  // Una gota: un tono corto que sube deprisa.
  { id: 'gota', notas: [{ frecuencia: 420, hasta: 1250, inicio: 0, duracion: 0.24, volumen: 0.85, ataque: 0.005 }] },
  // Dos tonos cortos, como un «tin-tin».
  {
    id: 'doble',
    notas: [
      { frecuencia: 880, inicio: 0, duracion: 0.14, volumen: 0.6, forma: 'triangle' },
      { frecuencia: 1174.7, inicio: 0.17, duracion: 0.18, volumen: 0.6, forma: 'triangle' },
    ],
  },
  // Un acorde grave que entra y sale despacio.
  {
    id: 'suave',
    notas: [
      { frecuencia: 523.25, inicio: 0, duracion: 1, volumen: 0.55, ataque: 0.08 },
      { frecuencia: 783.99, inicio: 0.02, duracion: 1, volumen: 0.32, ataque: 0.1 },
    ],
  },
  { id: 'ninguno', notas: [] },
];

export const CLAVE_SONIDO = 'oficina:avisos:sonido';
export const SONIDO_POR_DEFECTO: SonidoAviso = 'campanita';
export const CLAVE_VOLUMEN = 'oficina:avisos:volumen';
/** Por ciento. */
export const VOLUMEN_POR_DEFECTO = 80;
const EVENTO = 'oficina:avisos:sonido-cambiado';

const esSonido = (v: unknown): v is SonidoAviso => SONIDOS.some((s) => s.id === v);

/** El sonido elegido en este aparato (Campanita si no se ha elegido o el guardado no vale). */
export function sonidoElegido(): SonidoAviso {
  try {
    const v = localStorage.getItem(CLAVE_SONIDO);
    return esSonido(v) ? v : SONIDO_POR_DEFECTO;
  } catch {
    return SONIDO_POR_DEFECTO;
  }
}

export function elegirSonido(id: SonidoAviso): void {
  try {
    localStorage.setItem(CLAVE_SONIDO, id);
  } catch {
    // Sin almacenamiento: vale para esta visita (el estado de la hoja), no se recuerda.
  }
  window.dispatchEvent(new Event(EVENTO));
}

/** El volumen de los avisos en este aparato, 0-100 (80 si no se ha elegido o lo guardado no vale). */
export function volumenElegido(): number {
  try {
    const v = localStorage.getItem(CLAVE_VOLUMEN);
    const n = v === null || v.trim() === '' ? NaN : Number(v);
    return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : VOLUMEN_POR_DEFECTO;
  } catch {
    return VOLUMEN_POR_DEFECTO;
  }
}

export function elegirVolumen(n: number): void {
  try {
    localStorage.setItem(CLAVE_VOLUMEN, String(Math.min(100, Math.max(0, Math.round(n)))));
  } catch {
    // Sin almacenamiento: vale para esta visita, no se recuerda.
  }
  window.dispatchEvent(new Event(EVENTO));
}

/**
 * Toca un sonido (por defecto, el elegido) al volumen elegido (o a `volumen`, 0-100). true si sonó (o no había nada
 * que tocar: «Sin sonido» o volumen 0); false si el navegador no deja aún.
 */
export function tocarSonido(id: SonidoAviso = sonidoElegido(), volumen: number = volumenElegido()): Promise<boolean> {
  return tocarNotas(SONIDOS.find((s) => s.id === id)?.notas ?? [], { volumen: volumen / 100 });
}

/** El sonido elegido, al día si se cambia en otra parte de la página o en otra pestaña. */
export function useSonidoElegido(): [SonidoAviso, (id: SonidoAviso) => void] {
  const [sonido, setSonido] = useState(sonidoElegido);
  useEffect(() => {
    const releer = () => setSonido(sonidoElegido());
    window.addEventListener(EVENTO, releer);
    window.addEventListener('storage', releer);
    return () => {
      window.removeEventListener(EVENTO, releer);
      window.removeEventListener('storage', releer);
    };
  }, []);
  const elegir = useCallback((id: SonidoAviso) => {
    elegirSonido(id);
    setSonido(id);
  }, []);
  return [sonido, elegir];
}

/** El volumen elegido (0-100), al día si se cambia en otra parte de la página o en otra pestaña. */
export function useVolumenElegido(): [number, (n: number) => void] {
  const [volumen, setVolumen] = useState(volumenElegido);
  useEffect(() => {
    const releer = () => setVolumen(volumenElegido());
    window.addEventListener(EVENTO, releer);
    window.addEventListener('storage', releer);
    return () => {
      window.removeEventListener(EVENTO, releer);
      window.removeEventListener('storage', releer);
    };
  }, []);
  const elegir = useCallback((n: number) => {
    elegirVolumen(n);
    setVolumen(volumenElegido());
  }, []);
  return [volumen, elegir];
}
